import React, { useState, useContext } from 'react';
import { Context } from '../../services/contexts/StateContext';
import * as S from './styles';

const CATEGORIAS_ARQUIVOS = [
    'CHASSI',
    'FRENTE_DIR',
    'FRENTE_ESQ',
    'FUNDO_DIR',
    'FUNDO_ESQ',
    'MOTOR',
    'PAINEL',
    'KM PAINEL',
    'SELFIE',
    'VIDEOS'
];

const obterNomeArquivo = (arquivo = '') => arquivo.split('/').pop() || arquivo;

const normalizarTexto = (texto = '') => texto
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const ehVideo = (arquivo = '') => /\.mp4$/i.test(arquivo) || normalizarTexto(obterNomeArquivo(arquivo)).includes('VIDEO');

const obterCategoriaArquivo = (arquivo = '') => {
    if (ehVideo(arquivo)) {
        return 'VIDEOS';
    }

    const nomeNormalizado = normalizarTexto(obterNomeArquivo(arquivo));
    const categoriaEncontrada = CATEGORIAS_ARQUIVOS.find((categoria) => (
        categoria !== 'VIDEOS' && nomeNormalizado.includes(normalizarTexto(categoria))
    ));

    return categoriaEncontrada || 'OUTROS';
};

const ordenarArquivosPorNome = (arquivoA, arquivoB) => (
    obterNomeArquivo(arquivoA).localeCompare(obterNomeArquivo(arquivoB), 'pt-BR', {
        numeric: true,
        sensitivity: 'base'
    })
);

const estilos = {
    tituloPrincipal: {
        color: '#00ff9d',
        marginBottom: '10px',
        fontSize: '14px',
        fontWeight: 'bold'
    },
    blocoCategoria: {
        width: '100%',
        marginTop: '16px',
        padding: '14px 12px 16px',
        borderRadius: '10px',
        border: '1px solid rgba(0, 255, 157, 0.45)',
        background: 'linear-gradient(180deg, rgba(0, 255, 157, 0.12) 0%, rgba(19, 23, 31, 0.92) 100%)',
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)'
    },
    cabecalhoCategoria: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        marginBottom: '12px',
        paddingBottom: '8px',
        borderBottom: '1px solid rgba(0, 255, 157, 0.2)'
    },
    tituloCategoria: {
        color: '#cffff0',
        display: 'block',
        fontSize: '13px',
        fontWeight: 'bold',
        letterSpacing: '0.6px',
        textTransform: 'uppercase'
    },
    badgeQuantidade: {
        minWidth: '28px',
        padding: '3px 8px',
        borderRadius: '999px',
        backgroundColor: 'rgba(0, 255, 157, 0.18)',
        border: '1px solid rgba(0, 255, 157, 0.35)',
        color: '#00ff9d',
        fontSize: '11px',
        fontWeight: 'bold',
        textAlign: 'center'
    },
    listaArquivos: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        justifyContent: 'flex-start'
    },
    cardArquivo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
        position: 'relative',
        width: '110px',
        padding: '8px',
        borderRadius: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.24)',
        border: '1px solid rgba(0, 255, 157, 0.18)',
        transition: 'transform 0.2s ease, border-color 0.2s ease'
    },
    midiaBox: {
        width: '100px',
        height: '100px',
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        border: '2px solid #00ff9d',
        position: 'relative',
        overflow: 'hidden'
    },
    nomeArquivo: {
        fontSize: '10px',
        marginTop: '7px',
        color: '#d7ffef',
        textAlign: 'center',
        maxWidth: '100px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 'bold'
    }
};

//----------------------------------------------------
export function ExibirArquivosS3({ arquivos }) {
    const { state } = useContext(Context);
    const { evento } = state.evento;
    const listaArquivos = arquivos || evento.arquivos;
    
    const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
    const [tipoArquivo, setTipoArquivo] = useState('');

    // URL base do S3
    const S3_BASE_URL = 'https://rbento-web.s3.us-east-1.amazonaws.com/';

    // Função para abrir arquivo em tela cheia
    const abrirArquivo = (arquivo) => {
        const urlCompleta = `${S3_BASE_URL}${arquivo}`;
        
        // Verificar se é vídeo
        if (/\.mp4$/i.test(arquivo) || /video/i.test(arquivo)) {
            setTipoArquivo('video');
        } else {
            setTipoArquivo('imagem');
        }
        
        setArquivoSelecionado(urlCompleta);
    };

    // Função para fechar visualização
    const fecharVisualizacao = () => {
        setArquivoSelecionado(null);
        setTipoArquivo('');
    };

    // Se não houver arquivos, não exibir nada
    if (!listaArquivos || listaArquivos.length === 0) {
        return null;
    }

    const arquivosOrdenados = [...listaArquivos].sort(ordenarArquivosPorNome);
    const arquivosAgrupados = arquivosOrdenados.reduce((grupos, arquivo) => {
        const categoria = obterCategoriaArquivo(arquivo);

        if (!grupos[categoria]) {
            grupos[categoria] = [];
        }

        grupos[categoria].push(arquivo);

        return grupos;
    }, {});

    const categoriasParaExibir = [
        ...CATEGORIAS_ARQUIVOS.filter((categoria) => arquivosAgrupados[categoria]?.length),
        ...Object.keys(arquivosAgrupados).filter((categoria) => (
            categoria !== 'OUTROS' && !CATEGORIAS_ARQUIVOS.includes(categoria)
        )),
        ...(arquivosAgrupados.OUTROS?.length ? ['OUTROS'] : [])
    ];

    // ###########################################

    return (
        <>
        <S.Arquivos>
            <span style={estilos.tituloPrincipal}>
                Arquivos Salvos ({listaArquivos.length})
            </span>

            {categoriasParaExibir.map((categoria) => (
                <div key={categoria} style={estilos.blocoCategoria}>
                    <div style={estilos.cabecalhoCategoria}>
                        <span style={estilos.tituloCategoria}>
                            {categoria === 'VIDEOS' ? 'Videos' : categoria}
                        </span>
                        <span style={estilos.badgeQuantidade}>
                            {arquivosAgrupados[categoria].length}
                        </span>
                    </div>

                    <div style={estilos.listaArquivos}>
                        {arquivosAgrupados[categoria].map((arq, index) => {
                            const urlCompleta = `${S3_BASE_URL}${arq}`;
                            const isVideo = ehVideo(arq);

                            return (
                                <div
                                    key={`${categoria}-${arq}-${index}`}
                                    style={estilos.cardArquivo}
                                    onClick={() => abrirArquivo(arq)}
                                >
                                    {isVideo ? (
                                        <div style={estilos.midiaBox}>
                                            <video
                                                src={urlCompleta}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                            <div style={{
                                                position: 'absolute',
                                                fontSize: '30px',
                                                color: 'white',
                                                textShadow: '0 0 5px black'
                                            }}>
                                                ▶
                                            </div>
                                        </div>
                                    ) : (
                                        <img
                                            src={urlCompleta}
                                            alt={`Arquivo ${index + 1}`}
                                            style={{
                                                width: '100px',
                                                height: '100px',
                                                objectFit: 'cover',
                                                borderRadius: '8px',
                                                border: '2px solid #00ff9d'
                                            }}
                                        />
                                    )}
                                    <span style={estilos.nomeArquivo}>
                                        {obterNomeArquivo(arq)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </S.Arquivos>

        {/* Modal de visualização em tela cheia */}
        {arquivoSelecionado && (
        <S.Play>
            <S.BoxPlay>
                {tipoArquivo === 'video' ? (
                    <video
                        src={arquivoSelecionado}
                        controls
                        autoPlay
                        style={{
                            maxWidth: "90vw",
                            maxHeight: "80vh",
                            borderRadius: "10px"
                        }}
                    />
                ) : (
                    <img
                        src={arquivoSelecionado}
                        alt="Visualização"
                        style={{
                            maxWidth: "90vw",
                            maxHeight: "80vh",
                            borderRadius: "10px",
                            objectFit: "contain"
                        }}
                    />
                )}
                
                <button
                    onClick={fecharVisualizacao}
                    style={{
                        marginTop: "20px",
                        padding: "10px 30px",
                        fontSize: "16px",
                        fontWeight: "bold",
                        backgroundColor: "#f0433d",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer"
                    }}
                >
                    X FECHAR
                </button>
            </S.BoxPlay>
        </S.Play>
        )}
        </>
    );
}
